import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { usersInitialized as initializedUsers, getUsers } from 'store/slices/adminSlice';
import { getRegistrationList } from 'store/slices/registrationSlice';
import { useGetProjectsQuery } from 'store/api/projectApi';
import { useGetAllTitlesQuery, useGetInfringementsQuery } from 'store/api/titleApi';

// material-ui
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, ListSubheader, MenuItem, Grid } from '@mui/material';

// project imports
import config from 'config';
import { formatDateMMDDYY } from 'utils/dates';
import { titleText } from 'utils/titleIcon';
import { getRCRegStatus } from 'utils/status';
import { titlePath } from 'utils/routes';

const SearchPopUp = ({ open, handleClose, searchTerm }) => {
    const dispatch = useDispatch();
    const usersInitialized = useSelector(initializedUsers);
    const userIsAdmin = useSelector((state) => state?.account?.userIsAdmin);

    // Projects from RTK Query
    const { data: allProjects = [] } = useGetProjectsQuery();
    const projectList = useMemo(() => allProjects.filter((p) => p.name?.toLowerCase().includes(searchTerm)), [allProjects, searchTerm]);

    // Titles from V2 summary endpoint (lightweight, single request)
    const { data: titlesData, isLoading: titlesLoading } = useGetAllTitlesQuery();
    const allTitles = useMemo(() => titlesData?.titles || [], [titlesData]);
    const titleList = useMemo(() => allTitles.filter((t) => t.name?.toLowerCase().includes(searchTerm)), [allTitles, searchTerm]);

    // Registrations from old slice
    const registrationList = useSelector((state) =>
        state?.registration?.registrations?.filter((r) => r.regName?.toLowerCase().includes(searchTerm))
    );

    // Infringements/Actions from V2 infringements endpoint
    const { data: infringementsData, isLoading: infringementsLoading } = useGetInfringementsQuery();
    const { infringementList, actionList } = useMemo(() => {
        const infList = [];
        const actList = [];
        if (!infringementsData) return { infringementList: infList, actionList: actList };

        infringementsData.forEach((titleGroup) => {
            titleGroup.infringements?.forEach((inf) => {
                if (inf.reference?.toLowerCase().includes(searchTerm)) {
                    infList.push({
                        titleID: titleGroup.titleID,
                        titleName: titleGroup.titleName,
                        infringementID: inf.infringementID,
                        reference: inf.reference,
                        createdOn: inf.createdOn,
                        actions: inf.actions || []
                    });
                    // Flatten actions for matching infringements
                    inf.actions?.forEach((action, idx) => {
                        actList.push({
                            titleID: titleGroup.titleID,
                            infringementID: inf.infringementID,
                            actionID: idx,
                            titleName: titleGroup.titleName,
                            createdOn: action.createdOn,
                            type: action.type
                        });
                    });
                }
            });
        });

        return { infringementList: infList, actionList: actList };
    }, [infringementsData, searchTerm]);
    // User data for admin
    const userList = useSelector((state) =>
        state?.admin?.users.filter((user) => (user?.personName || '').toLowerCase().includes(searchTerm) && user.status === 'CONFIRMED')
    );

    const isLoading = titlesLoading || infringementsLoading;
    const nothingFound =
        !isLoading &&
        titleList.length === 0 &&
        projectList.length === 0 &&
        registrationList.length === 0 &&
        infringementList.length === 0 &&
        userList.length === 0;

    useEffect(() => {
        if (!open) return;
        if (!registrationList) {
            dispatch(getRegistrationList());
        }
        if (userIsAdmin && !usersInitialized) {
            dispatch(getUsers());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const getRegType = (type) => {
        const registrationType = config.regType.find((item) => item.id === type);
        return registrationType ? registrationType.name : '<unknown>';
    };

    const chooseTitle = (selectedTitle) => {
        const url = titlePath(selectedTitle.id, selectedTitle.projectID);
        handleClose({ url });
    };

    const chooseProject = (selectedProject) => {
        const url = `/project/${selectedProject.id}`;
        handleClose({ url });
    };

    const formatTitles = (titles) => {
        if (!titles || titles.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Titles</ListSubheader>
                {titles.map((title) => (
                    <MenuItem key={title.id} value={title} sx={{ mt: -1 }} onClick={() => chooseTitle(title)}>
                        {title.id} - {title.name} ({titleText(title.type)}), created: {formatDateMMDDYY(title.titleCreationDate)}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    const formatProjects = (projects) => {
        if (!projects || projects.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Projects</ListSubheader>
                {projects.map((project, index) => (
                    <MenuItem key={index} sx={{ mt: -1 }} onClick={() => chooseProject(project)}>
                        {project.id} - {project.name} ({titleText(project.type)}), number of titles: {project.numTitles}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    const chooseRegistration = (selectedRegistration) => {
        const url = `/register/title?id=${selectedRegistration.id}`;
        handleClose({ url });
    };

    const formatRegistrations = (registrations) => {
        if (!registrations || registrations.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Registrations</ListSubheader>
                {registrations.map((reg, index) => (
                    <MenuItem key={index} sx={{ mt: -1 }} onClick={() => chooseRegistration(reg)}>
                        {reg.id} - {reg.regName} ({getRegType(reg.regType)}), created: {formatDateMMDDYY(reg.createdOn)}, status:
                        {getRCRegStatus({ status: reg.status })}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    const chooseInfringement = (selectedInfringement) => {
        const url = `assess?title=${selectedInfringement.titleID}&inf=${selectedInfringement.infringementID}`;
        handleClose({ url });
    };

    const formatInfringements = (infringements) => {
        if (!infringements || infringements.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Infringements</ListSubheader>
                {infringements.map((inf, index) => (
                    <MenuItem key={index} sx={{ mt: -1 }} onClick={() => chooseInfringement(inf)}>
                        {inf.infringementID} - {inf.reference} (title: {inf.titleID} - {inf.titleName}), created:
                        {formatDateMMDDYY(inf.createdOn)}, actions: {inf.actions ? inf.actions.length : 0}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    const chooseAction = (selectedAction) => {
        const url = `assess?title=${selectedAction.titleID}&inf=${selectedAction.infringementID}`;
        handleClose({ url });
    };

    const formatActions = (actions) => {
        if (!actions || actions.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Actions</ListSubheader>
                {actions.map((action, index) => (
                    <MenuItem key={index} sx={{ mt: -1 }} onClick={() => chooseAction(action)}>
                        {action.actionID} - {action.type} (title: {action.titleID} - {action.titleName}), created:
                        {formatDateMMDDYY(action.createdOn)}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    const chooseUser = () => {
        const url = '/admin';
        handleClose({ url });
    };

    const formatUsers = (users) => {
        if (!users || users.length === 0) {
            return null;
        }
        return (
            <Grid item xs={12}>
                <ListSubheader>Users</ListSubheader>
                {users.map((user, index) => (
                    <MenuItem key={index} sx={{ mt: -1 }} onClick={() => chooseUser(user)}>
                        {`${user.personName} (${user.type}), confirmed: ${user.status === 'CONFIRMED' ? 'Yes' : 'No'}, active: ${
                            user.active ? 'Yes' : 'No'
                        }`}
                    </MenuItem>
                ))}
            </Grid>
        );
    };

    return (
        <div>
            <Dialog open={open} onClose={handleClose} fullWidth>
                <DialogTitle>Search RightsClick™</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {(() => {
                            if (isLoading) return 'Searching...';
                            if (nothingFound) return 'Nothing found';
                            return 'Please select desired entity:';
                        })()}
                    </DialogContentText>
                    <Grid container>
                        {formatProjects(projectList)}
                        {formatTitles(titleList)}
                        {formatRegistrations(registrationList)}
                        {formatInfringements(infringementList)}
                        {formatActions(actionList)}
                        {formatUsers(userList)}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="contained" size="small">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

SearchPopUp.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    searchTerm: PropTypes.string
};

export default SearchPopUp;
